// BUILD-042 Owner-led onboarding — HR Administration's "Documents & Signatures" destination
// for a specific Employee. Authority: docs/superpowers/decisions/
//   2026-10-07-build-042-owner-led-onboarding-documents-and-test-roster.md
//
// Paper print/sign/scan/upload workflow only (Owner's 23:23 simplification): HR assigns a
// pinned template version to the Employee, downloads/prints the blank form, and reviews a
// completed scan the Employee (or HR, on their behalf) uploads. No field authoring, no in-app
// typed-name signing, no generated PDF. Statuses shown exactly as the Owner specified:
// Awaiting return / Awaiting HR review / Needs correction / Reviewed complete. Upload alone
// is never shown as complete.
//
// October 10 12:24 Phoenix addendum (this pass) — root-cause correction to how a row presents
// itself, not to the underlying workflow rules, which are unchanged:
//   - Compact, single-line, EXPANDABLE rows (title/version/status/relevant date) replace the
//     prior always-expanded card — a row's actions/details only show once the row is clicked
//     open, so the page itself no longer always shows every upload control / review panel /
//     cancel input at once.
//   - THREE groups, not two: "Needs attention" (actionable), "Completed records" (reviewed
//     complete — kept separately visible, never silently folded into History just because the
//     document happened to be completed), and "History" (actually cancelled — this build has
//     no file-level supersession relation, so History holds exactly cancelled records; it does
//     not guess at a "current vs superseded" relationship that does not exist in the data).
//   - On a completed record, the PRIMARY action is View/Download the completed copy (the
//     uploaded, HR-reviewed scan) — the original blank template is now a SECONDARY "Original
//     assigned form" link in the expanded detail, not the headline action.
//   - Preview (opens inline in a new tab) and Download (forces a named save) are two distinct
//     actions on the same file — not one ambiguous link — reusing the same disposition switch
//     the Form Library now has.
//   - Dates are never relabeled: documentDateLabel.ts (shared with DocumentsSelfSection) picks
//     the most specific TRUE fact on the record — reported paper-signed date, else HR's review
//     date, else the upload date, else the assignment date as a last resort — and labels it
//     exactly as what it is. "Assigned" is never shown as if it were "Signed."
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  listHrDocumentTemplates, listHrDocumentAssignmentsForEmployee, assignHrDocument, listHrLiveSigningConfigs,
  downloadHrDocumentTemplate, downloadHrDocumentSubmission, reviewHrDocumentSubmission,
  uploadHrDocumentSubmission, cancelHrDocumentAssignment,
  restoreHrDocumentAssignmentFromHistory, listHrDocumentHistoryEvents,
  type HrDocumentTemplate, type HrDocumentAssignment, type HrDocumentHistoryEvent, type HrAssignmentPrompt, type HrAssignmentSummary,
} from '../../../api/client';
import { AssignValueInputs } from '../AssignValueInputs';
import { missingAssignmentValues } from '../formFieldsModel';
import { CardTitle } from './SectionHeading';
import { DocumentUploadAction } from './DocumentUploadAction';
import { checkDownloadUrl } from './documentFileCheck';
import { documentDateLabel } from './documentDateLabel';
import { MoveToHistoryModal } from './MoveToHistoryModal';
import { EquipmentCheckoutPanel } from './EquipmentCheckoutPanel';
import { SigningPanel } from './SigningPanel';
import { useHrAccess } from '../HrAccessContext';
import { formatLocalDate, formatLocalDateTime } from '../hrFormat';
import { actionErrorMessage, classifyLoadError, needsPermissionMessage, PERMISSION_LABEL, useSurfaceFlag, type LoadView } from './loadState';
import { LoadViewNotice } from './LoadNotices';
import { formRows, selectableIds } from '../setup/documentsModel';
import { clearRetained, droppedNote, loadRetained, postSetupScope, revalidateRetained, saveRetained, type RetentionStorage } from '../setup/documentsRetention';

/** sessionStorage only (per tab); null when the browser refuses it (the section then simply remembers nothing). */
function tabStorage(): RetentionStorage | null {
  try { return typeof window !== 'undefined' ? window.sessionStorage : null; } catch { return null; }
}

const STATUS_LABEL: Record<HrDocumentAssignment['status'], string> = {
  awaiting_return: 'Awaiting return',
  awaiting_hr_review: 'Awaiting HR review',
  needs_correction: 'Needs correction',
  reviewed_complete: 'Reviewed complete',
  cancelled: 'Cancelled',
};
const STATUS_COLOR: Record<HrDocumentAssignment['status'], string> = {
  awaiting_return: 'text-gray-500 bg-gray-50',
  awaiting_hr_review: 'text-amber-700 bg-amber-50',
  needs_correction: 'text-red-700 bg-red-50',
  reviewed_complete: 'text-green-700 bg-green-50',
  cancelled: 'text-gray-400 bg-gray-50',
};
const ACTIONABLE_STATUSES: HrDocumentAssignment['status'][] = ['awaiting_return', 'awaiting_hr_review', 'needs_correction'];

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** October 10 14:05 addendum, renamed/corrected October 10 18:22 addendum — shows WHY/by whom/
 *  when a record was archived, plus a confirmed Restore with its outcome shown INLINE, right
 *  here, rather than only in a status line elsewhere on the page. Loads the assignment's
 *  history events on mount (lazily, once). The reason snapshot is shown exactly as it was
 *  recorded at archive time — never re-fetched from the live reason registry, which may have
 *  since been edited/retired/reactivated. */
function HistoryEventDetail({
  assignment, events, onLoad, correctionNote, onCorrectionNoteChange, onRestore, restoring, feedback,
}: {
  assignment: HrDocumentAssignment;
  events: HrDocumentHistoryEvent[] | undefined;
  onLoad: () => void;
  correctionNote: string;
  onCorrectionNoteChange: (v: string) => void;
  onRestore: () => void;
  restoring: boolean;
  feedback?: { kind: 'success' | 'conflict' | 'error'; message: string };
}) {
  useEffect(() => { onLoad(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const current = events?.find((e) => e.eventId === assignment.currentHistoryEventId) ?? events?.[0];
  if (events === undefined) {
    return <div className="mt-2 text-xs text-gray-400">Loading archive detail…</div>;
  }
  if (!current) {
    return <div className="mt-2 text-xs text-gray-400">No archive detail found for this record.</div>;
  }
  // Once a Restore SUCCEEDS, this record is no longer archived — the parent's refresh() will
  // move it back to Completed records on the next render. Keep the success message visible
  // here one more render instead of it vanishing the instant the row disappears, so the admin
  // actually sees the confirmation rather than a silent re-render.
  const restoredAway = feedback?.kind === 'success';

  return (
    <div className="mt-2 bg-gray-50 rounded-md p-3 text-xs text-gray-700 space-y-1.5">
      <div><span className="font-medium text-gray-900">{current.reasonLabelSnapshot}</span> — archived by {current.actorEmployeeId} on {formatLocalDateTime(current.occurredAt)}</div>
      <div className="text-gray-500">{current.reasonExplanationSnapshot}</div>
      {current.detailText && <div>Note: {current.detailText}</div>}
      {current.linkedAssignmentId && <div>Linked record: {current.linkedAssignmentId}</div>}
      {current.equipmentReturnDate && (
        <div>Equipment returned {current.equipmentReturnDate}, received by {current.equipmentReceivedBy}, identified as "{current.equipmentIdentified}" — whole issuance confirmed.</div>
      )}
      {current.filedInErrorConfirmed && <div>Confirmed filed in error.</div>}

      {restoredAway ? (
        <div className="pt-1.5 border-t border-gray-200 mt-1.5 text-green-700 font-medium">
          ✓ {feedback!.message}
        </div>
      ) : (
        <div className="pt-1.5 border-t border-gray-200 mt-1.5">
          <label className="text-gray-500 block mb-1">Correction note (optional, for "restored by mistake")</label>
          <div className="flex gap-2 items-center flex-wrap">
            <input
              type="text"
              value={correctionNote}
              onChange={(e) => onCorrectionNoteChange(e.target.value)}
              placeholder="e.g. Selected the wrong record"
              className="border border-gray-300 rounded px-2 py-1 text-xs w-56"
            />
            <button type="button" onClick={onRestore} disabled={restoring} className="text-[#4AABDE] hover:underline disabled:opacity-50">
              {restoring ? 'Restoring…' : 'Restore (archived by mistake)'}
            </button>
          </div>
          {/* October 10 18:22 addendum root-cause fix: a 409 conflict is a LEGITIMATE outcome
              (e.g. the replacement/duplicate this points to is still active, or an equipment-
              returned archive can never be restored) — shown plainly beside the button, with
              what to do next, never a silent no-op. An unexpected error gets the same
              treatment but in red, since it is NOT an expected/permitted state. */}
          {feedback?.kind === 'conflict' && (
            <div className="mt-1.5 text-amber-700">⚠ {feedback.message}</div>
          )}
          {feedback?.kind === 'error' && (
            <div className="mt-1.5 text-red-700">{feedback.message}</div>
          )}
        </div>
      )}
    </div>
  );
}

/** Keyed by the employee, so an in-progress assign choice can never carry over from one person to another. */
export function AdminDocumentsSection(props: { employeeId: string; focusAssignmentId?: string | null; /** Oct 24 guided setup: Equipment has its own step, so the equipment checkout editor is not drawn here. */ hideEquipment?: boolean }) {
  return <AdminDocumentsSectionBody key={props.employeeId} {...props} />;
}

function AdminDocumentsSectionBody({ employeeId, focusAssignmentId, hideEquipment }: { employeeId: string; focusAssignmentId?: string | null; hideEquipment?: boolean }) {
  const { state: accessState } = useHrAccess();
  const callerEmployeeId = accessState.kind === 'ready' ? accessState.me.employeeId : null;
  // Cycle 12: opened from the guided setup's Documents step ("View →"): a one-click way back to that setup step (router state only; nothing is stored).
  const navigate = useNavigate();
  const cameFrom = (useLocation().state as { returnTo?: string; employeeName?: string } | null) ?? null;
  const backToSetup = cameFrom?.returnTo && cameFrom.returnTo.startsWith(`/hr/admin/employees/${encodeURIComponent(employeeId)}/setup`) ? cameFrom.returnTo : null;
  const [templates, setTemplates] = useState<HrDocumentTemplate[]>([]);
  const [assignments, setAssignments] = useState<HrDocumentAssignment[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);
  // Expanded fields (Oct 16): the values HR types for fields filled in at assignment time, and which prompts the chosen form has.
  const [livePrompts, setLivePrompts] = useState<Record<string, HrAssignmentPrompt[]>>({});
  // Oct 17: who fills in the rest of the chosen form (employee while signing / copied from the record), shown beside HR's own inputs.
  const [liveSummaries, setLiveSummaries] = useState<Record<string, HrAssignmentSummary | undefined>>({});
  const [assignValues, setAssignValues] = useState<Record<string, string>>({});
  const [reviewNote, setReviewNote] = useState<Record<string, string>>({});
  const [cancelReason, setCancelReason] = useState<Record<string, string>>({});
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [downloadNotice, setDownloadNotice] = useState<Record<string, string>>({});
  const [historyOpen, setHistoryOpen] = useState(false);
  const [completedOpen, setCompletedOpen] = useState(true);
  // October 10 18:22 addendum root-cause fix — "known invalid legacy samples must show
  // unavailable rather than working-looking buttons." Proactively checks the PRIMARY file's
  // real bytes once a row is expanded, instead of only discovering invalidity after a click.
  // Keyed by the same submission/template keys handleOpen already uses, so a result found here
  // is reused rather than re-fetched when the admin actually clicks View/Download.
  const [fileValidity, setFileValidity] = useState<Record<string, { valid: boolean; reason?: string }>>({});
  // October 10 12:24 addendum: compact, EXPANDABLE rows — a row's actions/details are hidden
  // until it is expanded. Needs-attention rows default open (they need a next step right now);
  // completed/history rows default closed.
  const [expandedId, setExpandedId] = useState<string | null>(focusAssignmentId ?? null);

  // Oct 24: the forms list (for the Assign control) and this employee's assignments are both capability-gated reads. Loading / ready (possibly empty) / denied /
  // unavailable are kept apart so a refusal or an outage is never drawn as "No documents assigned yet." A failed RE-read keeps what is already shown.
  const [templatesView, setTemplatesView] = useState<LoadView>({ kind: 'loading' });
  const [assignmentsView, setAssignmentsView] = useState<LoadView>({ kind: 'loading' });
  const mayManageDocuments = useSurfaceFlag('mayManageDocuments');
  const cannotAssign = mayManageDocuments === false;
  const assignPermissionMessage = needsPermissionMessage(PERMISSION_LABEL.documents, 'assign documents');
  const refresh = () => {
    listHrDocumentTemplates()
      .then((d) => { setTemplates(d.templates.filter((t) => !t.retiredAt)); setTemplatesView({ kind: 'ready' }); })
      .catch((e) => setTemplatesView((v) => (v.kind === 'ready' ? v : classifyLoadError(e, { gatedList: true }))));
    listHrDocumentAssignmentsForEmployee(employeeId)
      .then((d) => { setAssignments(d.assignments); setAssignmentsView({ kind: 'ready' }); })
      .catch((e) => setAssignmentsView((v) => (v.kind === 'ready' ? v : classifyLoadError(e, { targeted: true }))));
  };
  useEffect(() => { setTemplatesView({ kind: 'loading' }); setAssignmentsView({ kind: 'loading' }); setAssignments([]); refresh(); }, [employeeId]);
  useEffect(() => {
    listHrLiveSigningConfigs().then((d) => {
      setLivePrompts(Object.fromEntries(d.configs.map((c) => [c.templateVersionId, c.assignmentPrompts ?? []])));
      setLiveSummaries(Object.fromEntries(d.configs.map((c) => [c.templateVersionId, c.assignmentSummary])));
    }).catch(() => { setLivePrompts({}); setLiveSummaries({}); });
  }, [employeeId]);
  // Cycle 13: the form HR picked in the Assign control is remembered in this tab's sessionStorage (caller + employee scoped, 2h, template id only) so it survives
  // leaving this page and coming back. On return it is re-checked against the freshly loaded lists: a retired or already-assigned form is dropped, a caller who may no
  // longer assign gets nothing restored, and nothing is ever assigned by restoring. HR-entered values are not kept.
  const retentionScope = postSetupScope(callerEmployeeId, employeeId);
  const retentionOn = Boolean(callerEmployeeId) && mayManageDocuments === true;
  const [retentionReady, setRetentionReady] = useState(false);
  useEffect(() => {
    if (retentionReady || templatesView.kind !== 'ready' || assignmentsView.kind !== 'ready') return;
    if (mayManageDocuments === false) clearRetained(tabStorage(), retentionScope);
    const kept = retentionOn ? loadRetained(tabStorage(), retentionScope, Date.now()) : null;
    if (kept) {
      const back = revalidateRetained(kept, selectableIds(formRows(templates, assignments)), true);
      const id = [...back.selected][0];
      if (id) setSelectedTemplateId(id);
      const note = droppedNote(back.dropped);
      if (note) setStatus(note);
    }
    setRetentionReady(true);
  }, [retentionReady, templatesView.kind, assignmentsView.kind, templates, assignments, mayManageDocuments, retentionOn, retentionScope.callerId, retentionScope.employeeId]);
  useEffect(() => {
    if (!retentionReady || !retentionOn) return;
    saveRetained(tabStorage(), retentionScope, { selected: selectedTemplateId ? [selectedTemplateId] : [], search: '', panelOpen: false }, Date.now());
  }, [retentionReady, retentionOn, selectedTemplateId, retentionScope.callerId, retentionScope.employeeId]);
  const chosenTemplate = templates.find((t) => t.templateId === selectedTemplateId);
  const chosenPrompts = chosenTemplate?.versions[0] ? (livePrompts[chosenTemplate.versions[0].versionId] ?? []) : [];
  const chosenSummary = chosenTemplate?.versions[0] ? (liveSummaries[chosenTemplate.versions[0].versionId] ?? null) : null;

  async function handleHrUpload(assignmentId: string, file: File) {
    const fileContentBase64 = await fileToBase64(file);
    const contentType = file.type || 'application/pdf';
    await uploadHrDocumentSubmission(assignmentId, { fileName: file.name, fileContentBase64, contentType });
    // The upload success moves this row's status to awaiting_hr_review, which immediately
    // unmounts DocumentUploadAction (only rendered for awaiting_return/needs_correction) —
    // its own inline success message would never actually be seen. Surface the confirmation
    // at the section level instead, which survives the status change.
    setStatus(`Uploaded "${file.name}". Status is now Awaiting HR review.`);
    refresh();
  }

  async function handleCancel(assignmentId: string) {
    setCancellingId(assignmentId);
    try {
      await cancelHrDocumentAssignment(assignmentId, cancelReason[assignmentId] || undefined);
      setStatus('Assignment cancelled.');
      setCancelReason((m) => ({ ...m, [assignmentId]: '' }));
      refresh();
    } catch (e: any) {
      setStatus(e.message || 'Failed to cancel the assignment.');
    } finally {
      setCancellingId(null);
    }
  }

  async function handleAssign() {
    if (!selectedTemplateId || assigning) return;
    const template = templates.find((t) => t.templateId === selectedTemplateId);
    const latestVersion = template?.versions[0];
    if (!latestVersion) return;
    const prompts = livePrompts[latestVersion.versionId] ?? [];
    const missing = missingAssignmentValues(prompts, assignValues);
    if (missing.length) { setStatus(`Enter ${missing.join(', ')} before assigning this document.`); return; }
    setAssigning(true);
    try {
      await assignHrDocument(employeeId, selectedTemplateId, latestVersion.versionId, Object.fromEntries(prompts.map((p) => [p.fieldKey, (assignValues[p.fieldKey] ?? '').trim()]).filter(([, v]) => v)));
      setAssignValues({});
      setStatus(`Assigned "${template.name}" (version ${latestVersion.versionNumber}).`);
      setSelectedTemplateId('');
      refresh();
    } catch (e: any) {
      setStatus(actionErrorMessage(e, PERMISSION_LABEL.documents, 'assign documents', 'Failed to assign document.'));
    } finally {
      setAssigning(false);
    }
  }

  // October 8 21:19 addendum: never silently offer a download that will not open. Fetch the
  // actual bytes behind the presigned URL and run a real structural check before opening it,
  // rather than trusting the server's declared Content-Type (the known-invalid historical
  // fixtures were ALSO served as application/pdf).
  // October 10 12:24 addendum: Preview (inline, new tab) and Download (attachment, named save)
  // are two distinct actions against the same file — same check either way.
  async function handleOpen(key: string, getUrl: (disposition: 'inline' | 'attachment') => Promise<{ url: string; fileName: string }>, disposition: 'inline' | 'attachment') {
    setDownloadNotice((m) => ({ ...m, [key]: disposition === 'inline' ? 'Checking file…' : 'Preparing download…' }));
    try {
      const { url, fileName } = await getUrl(disposition);
      const result = await checkDownloadUrl(url);
      if (!result.looksValid) {
        setDownloadNotice((m) => ({ ...m, [key]: `"${fileName}" could not be opened: ${result.reason}` }));
        return;
      }
      setDownloadNotice((m) => ({ ...m, [key]: '' }));
      window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      setDownloadNotice((m) => ({ ...m, [key]: e.message || 'Failed to prepare the file.' }));
    }
  }

  // October 10 18:22 addendum root-cause fix — "known invalid legacy samples must show
  // unavailable rather than working-looking buttons." Proactively runs the SAME real-byte
  // structural check handleOpen already uses, but at expand time rather than click time, for
  // the row's PRIMARY file (the completed copy if one exists, otherwise the blank template).
  // Never re-checks a file whose validity is already cached for this key. This never fetches
  // the secondary "Record details" original-form file unless that <details> is itself opened
  // (handleOpen there performs its own check on click, same as before) — this stays bounded to
  // the one file that actually matters for routine viewing.
  async function checkPrimaryFile(
    a: HrDocumentAssignment,
    currentSubmission: HrDocumentAssignment['submissions'][number] | undefined,
    submissionKey: string | null,
    templateKey: string,
    hasCompletedCopy: unknown
  ) {
    const key = hasCompletedCopy ? submissionKey! : templateKey;
    if (fileValidity[key]) return;
    try {
      const { url } = hasCompletedCopy
        ? await downloadHrDocumentSubmission(currentSubmission!.submissionId, 'inline')
        : await downloadHrDocumentTemplate(a.assignmentId, 'inline');
      const result = await checkDownloadUrl(url);
      setFileValidity((m) => ({ ...m, [key]: { valid: result.looksValid, reason: result.reason } }));
    } catch (e: any) {
      setFileValidity((m) => ({ ...m, [key]: { valid: false, reason: e.message || 'Could not check this file.' } }));
    }
  }

  // October 10 14:05 addendum: Move-to-History modal state + per-assignment history-event
  // cache (fetched on demand when a History row is expanded, so the reason/detail/actor/time
  // show without an always-on extra request for every row).
  const [moveToHistoryTarget, setMoveToHistoryTarget] = useState<HrDocumentAssignment | null>(null);
  const [historyEvents, setHistoryEvents] = useState<Record<string, HrDocumentHistoryEvent[]>>({});
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [correctionNote, setCorrectionNote] = useState<Record<string, string>>({});
  // October 10 18:22 addendum root-cause fix — "prevent silent repeated clicks; show progress/
  // success/error RIGHT NEXT TO the Restore control, not only in the far-away top-of-card
  // status line." A conflict (409, e.g. the linked replacement is still active) is a LEGITIMATE
  // outcome, not a failure to hide — it must stay visible, inline, with the server's own exact
  // explanation, until the admin takes a different action (never auto-dismissed on a timer).
  const [restoreFeedback, setRestoreFeedback] = useState<Record<string, { kind: 'success' | 'conflict' | 'error'; message: string }>>({});

  async function loadHistoryEvents(assignmentId: string) {
    if (historyEvents[assignmentId]) return;
    try {
      const d = await listHrDocumentHistoryEvents(assignmentId);
      setHistoryEvents((m) => ({ ...m, [assignmentId]: d.events }));
    } catch {
      setHistoryEvents((m) => ({ ...m, [assignmentId]: [] }));
    }
  }

  async function handleRestore(assignmentId: string) {
    // Guard against a second click firing while the first request is still in flight — the
    // button is also disabled below, but this is the authoritative guard.
    if (restoringId === assignmentId) return;
    setRestoringId(assignmentId);
    setRestoreFeedback((m) => ({ ...m, [assignmentId]: undefined as any }));
    try {
      await restoreHrDocumentAssignmentFromHistory(assignmentId, correctionNote[assignmentId] || undefined);
      setRestoreFeedback((m) => ({ ...m, [assignmentId]: { kind: 'success', message: 'Restored — moved back to Completed records.' } }));
      setCorrectionNote((m) => ({ ...m, [assignmentId]: '' }));
      refresh();
    } catch (e: any) {
      // A 409 CONFLICT is the server's legitimate refusal (e.g. the replacement/duplicate it
      // points to is still active, or an equipment-returned archive can never be restored at
      // all) — distinguished from an unexpected error so the admin sees "this is blocked, here
      // is why, here is what you can do" rather than a generic failure.
      const kind = e?.status === 409 ? 'conflict' : 'error';
      setRestoreFeedback((m) => ({ ...m, [assignmentId]: { kind, message: e.message || 'Failed to restore this record.' } }));
    } finally {
      setRestoringId(null);
    }
  }

  const needsReview = assignments.filter((a) => a.status === 'awaiting_hr_review');
  const waitingOnEmployee = assignments.filter((a) => a.status === 'awaiting_return' || a.status === 'needs_correction');
  // October 10 12:24 addendum: "Completed records" is its OWN group, not folded into History —
  // a reviewed-complete document is still available/current, not an archived record.
  // October 10 14:05 addendum: a reviewed-complete record only counts as "Completed" while it
  // is NOT currently placed in History (movedToHistoryAt is null) — placement is independent
  // of status.
  const completed = assignments.filter((a) => a.status === 'reviewed_complete' && !a.movedToHistoryAt);
  // October 10 18:22 addendum: renamed "History" -> "Archived records." Holds what is
  // ACTUALLY cancelled OR explicitly archived (moved to History) by an authorized admin action
  // — never a guessed "current vs superseded" relationship. Archived and Cancelled remain two
  // visibly distinct outcomes within this one group (see the badge logic in renderRow below) —
  // they are grouped together only because both are "not the current actionable/completed
  // record," not because they mean the same thing.
  const archivedRecords = assignments.filter((a) => a.status === 'cancelled' || (a.status === 'reviewed_complete' && a.movedToHistoryAt));

  function renderRow(a: HrDocumentAssignment) {
    const currentSubmission = a.submissions.find((s) => s.submissionId === a.currentSubmissionId) ?? a.submissions[0];
    const templateKey = `template-${a.assignmentId}`;
    const submissionKey = currentSubmission ? `submission-${currentSubmission.submissionId}` : null;
    const { label: dateLabel, dateText } = documentDateLabel(a, currentSubmission);
    const expanded = expandedId === a.assignmentId;
    // An electronically signed equipment form is already the real, retained signed copy while HR reviews it, so it is offered
    // right away (labelled as awaiting review). Paper uploads keep the existing rule: only HR-approved copies are primary.
    const isEquipment = !!a.equipmentCheckout;
    // Oct 15 22:12: an ordinary uploaded-PDF form with a pinned signing setup is e-signable exactly like an equipment form.
    const isESign = isEquipment || !!a.documentSigning;
    const eSignedAwaitingReview = a.status === 'awaiting_hr_review' && isESign;
    const hasCompletedCopy = (a.status === 'reviewed_complete' || eSignedAwaitingReview) && currentSubmission && submissionKey;
    // For an equipment checkout the "original" is the employee-specific populated PDF as issued; for an e-signable uploaded form it is the
    // Form Library file the assignment was pinned to. Neither is offered to the employee once signed; HR keeps it under Record details.
    const originalLabel = isEquipment ? 'Unsigned issued copy' : isESign ? 'Unsigned original form' : 'Original assigned form';
    // An e-signable form that should have a signed copy but does not: say so. The unsigned file is NEVER offered in its place.
    const equipmentSignedMissing = isESign && (a.status === 'awaiting_hr_review' || a.status === 'reviewed_complete') && !currentSubmission;
    // October 10 18:22 addendum: an archived (moved-to-History) record is still technically
    // status='reviewed_complete' — show a distinct "Archived" badge instead of the plain
    // "Reviewed complete" badge so it never looks identical to a current completed record in
    // the row list, keeping Archived clearly distinguished from Cancelled too (separate badge
    // colors, separate wording).
    const isArchived = a.status === 'reviewed_complete' && !!a.movedToHistoryAt;
    const badgeLabel = isArchived ? 'Archived' : STATUS_LABEL[a.status];
    const badgeColor = isArchived ? 'text-gray-600 bg-gray-100' : STATUS_COLOR[a.status];

    return (
      <li key={a.assignmentId} className="py-2.5">
        {/* Compact single-line summary row — click to expand. */}
        <button
          type="button"
          onClick={() => {
            const next = expanded ? null : a.assignmentId;
            setExpandedId(next);
            // October 10 18:22 addendum: proactively check the PRIMARY file's real bytes the
            // moment a row is opened, rather than waiting for a click on View/Download — a
            // known-invalid legacy sample shows "unavailable" immediately, never a
            // working-looking button the admin then clicks only to be told it failed.
            if (next) checkPrimaryFile(a, currentSubmission, submissionKey, templateKey, hasCompletedCopy);
          }}
          className="w-full flex items-center justify-between gap-3 flex-wrap text-left"
          aria-expanded={expanded}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-gray-400 text-xs flex-shrink-0">{expanded ? '▾' : '▸'}</span>
            <span className="text-sm font-semibold text-gray-900 truncate">{a.templateName}</span>
            <span className="text-xs text-gray-400 flex-shrink-0">v{a.versionNumber}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-gray-400">{dateLabel} {dateText}</span>
            <span className={`text-xs font-medium px-2 py-1 rounded-md ${badgeColor}`}>{badgeLabel}</span>
          </div>
        </button>

        {expanded && (
          <div className="mt-2 pl-5">
            {/* October 10 18:22 addendum: the completed copy is now the ONLY primary action on
                a completed/archived record — the original blank-form View/Download buttons no
                longer appear in the routine view at all once a completed copy exists (they
                move under the collapsed "Record details" below). A not-yet-complete
                (pending/awaiting) assignment still shows the blank form directly, since it is
                the only relevant file at that point. */}
            {(() => {
              const primaryKey = hasCompletedCopy ? submissionKey! : templateKey;
              const primaryValidity = fileValidity[primaryKey];
              const primaryUnavailable = primaryValidity && !primaryValidity.valid;
              return (
                <div className="flex gap-3 text-xs items-center flex-wrap">
                  <span className="text-gray-500">{equipmentSignedMissing ? 'Signed copy:' : hasCompletedCopy ? (eSignedAwaitingReview ? 'Signed copy (awaiting your review):' : 'Completed copy:') : isESign ? `${originalLabel}:` : 'Blank form:'}</span>
                  {equipmentSignedMissing ? (
                    <span className="text-gray-400 italic" data-testid="signed-copy-unavailable">Unavailable — no signed copy is on file for this record. The unsigned issued copy is under Record details.</span>
                  ) : primaryUnavailable ? (
                    <span className="text-gray-400 italic">Unavailable — {primaryValidity!.reason}</span>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => handleOpen(primaryKey, hasCompletedCopy ? (d) => downloadHrDocumentSubmission(currentSubmission!.submissionId, d) : (d) => downloadHrDocumentTemplate(a.assignmentId, d), 'inline')}
                        className="text-[#4AABDE] hover:underline font-medium"
                      >
                        View
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOpen(primaryKey, hasCompletedCopy ? (d) => downloadHrDocumentSubmission(currentSubmission!.submissionId, d) : (d) => downloadHrDocumentTemplate(a.assignmentId, d), 'attachment')}
                        className="text-[#4AABDE] hover:underline font-medium"
                      >
                        Download
                      </button>
                    </>
                  )}
                </div>
              );
            })()}
            {submissionKey && downloadNotice[submissionKey] && (
              <div className="mt-1 text-xs text-amber-700">{downloadNotice[submissionKey]}</div>
            )}
            {!hasCompletedCopy && downloadNotice[templateKey] && (
              <div className="mt-1 text-xs text-amber-700">{downloadNotice[templateKey]}</div>
            )}
            {/* Oct 17: say plainly which file View opens. Before the employee has signed, it is the ORIGINAL form from the Form Library,
                untouched - it does not contain the values HR entered at assignment or anything the employee types. Those live only in the
                signed copy. The original is never overwritten. */}
            {isESign && !hasCompletedCopy && !equipmentSignedMissing && (
              <p className="mt-1 text-[11px] text-gray-500" data-testid="original-explainer">
                View opens the original form exactly as it is in the Form Library. It does not show the information HR entered when assigning it, or anything the employee types. Those appear only in the signed copy, which is available once the employee has signed. The original is never changed.
              </p>
            )}
            {isESign && hasCompletedCopy && (
              <p className="mt-1 text-[11px] text-gray-500" data-testid="signed-explainer">
                View opens the signed copy, which includes the information entered at assignment and the employee's answers. The unsigned original is kept untouched under Record details.
              </p>
            )}

            {/* BUILD-042 equipment checkout: electronic-signing state, witness action and HR cancel.
                Present only for an assignment created by Generate & assign. */}
            {isESign && (
              <SigningPanel assignment={a} mode="admin" callerEmployeeId={callerEmployeeId} onChanged={refresh} />
            )}

            {/* October 8 addenda root-cause fix: HR may also upload a scan it received directly,
                on the Employee's behalf, through a clearly-labeled action with filename/progress/
                result feedback — never a bare auto-submitting file input. */}
            {(a.status === 'awaiting_return' || a.status === 'needs_correction') && (
              isESign ? (
                <details className="mt-2 text-xs" data-testid="paper-fallback">
                  <summary className="text-gray-400 cursor-pointer hover:text-gray-600">Upload a paper-signed scan on the employee's behalf instead</summary>
                  <div className="mt-2"><DocumentUploadAction onUpload={(file) => handleHrUpload(a.assignmentId, file)} /></div>
                </details>
              ) : (
                <div className="mt-2">
                  <DocumentUploadAction onUpload={(file) => handleHrUpload(a.assignmentId, file)} />
                </div>
              )
            )}
            {/* October 8 addenda: reversible cancellation for a mistaken outstanding assignment —
                never reachable once reviewed_complete or already cancelled. */}
            {ACTIONABLE_STATUSES.includes(a.status) && (
              <div className="flex gap-2 mt-2 text-xs items-center flex-wrap">
                <input
                  type="text"
                  placeholder="Cancellation reason (optional)"
                  value={cancelReason[a.assignmentId] ?? ''}
                  onChange={(e) => setCancelReason((m) => ({ ...m, [a.assignmentId]: e.target.value }))}
                  className="border border-gray-300 rounded px-2 py-1 text-xs w-44"
                />
                <button
                  type="button"
                  onClick={() => handleCancel(a.assignmentId)}
                  disabled={cancellingId === a.assignmentId}
                  className="text-red-600 hover:underline disabled:opacity-50"
                >
                  {cancellingId === a.assignmentId ? 'Cancelling…' : 'Cancel this assignment'}
                </button>
              </div>
            )}
            {currentSubmission && (a.status === 'awaiting_hr_review') && (
              <div className="mt-2 bg-[#EDF5FC] rounded-md p-3">
                <div className="text-xs text-gray-600 mb-2">
                  Uploaded {formatLocalDateTime(currentSubmission.uploadedAt)}
                  {currentSubmission.reportedSignedDate && (
                    <> · employee reported signing it on paper on {formatLocalDate(currentSubmission.reportedSignedDate)} (manually reported, not electronically verified)</>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="Review note (optional)"
                  value={reviewNote[currentSubmission.submissionId] ?? ''}
                  onChange={(e) => setReviewNote((m) => ({ ...m, [currentSubmission.submissionId]: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-xs mb-2"
                />
                <div className="flex gap-2">
                  <button type="button" onClick={() => reviewHrDocumentSubmission(currentSubmission.submissionId, 'approved', reviewNote[currentSubmission.submissionId] || undefined).then(() => { setStatus('Marked reviewed complete.'); refresh(); }).catch((e: any) => setStatus(e.message || 'Failed to record the review.'))} className="bg-green-600 text-white text-xs px-3 py-1.5 rounded">
                    Mark reviewed complete
                  </button>
                  <button type="button" onClick={() => reviewHrDocumentSubmission(currentSubmission.submissionId, 'needs_correction', reviewNote[currentSubmission.submissionId] || undefined).then(() => { setStatus('Marked needs correction.'); refresh(); }).catch((e: any) => setStatus(e.message || 'Failed to record the review.'))} className="bg-red-600 text-white text-xs px-3 py-1.5 rounded">
                    Needs correction
                  </button>
                </div>
              </div>
            )}
            {a.status === 'needs_correction' && currentSubmission?.reviewNote && (
              <div className="mt-2 bg-red-50 rounded-md p-3 text-xs text-red-700">
                HR noted: {currentSubmission.reviewNote}
              </div>
            )}
            {a.status === 'cancelled' && (
              <div className="mt-2 bg-gray-50 rounded-md p-3 text-xs text-gray-500">
                Cancelled {a.cancelledAt && formatLocalDateTime(a.cancelledAt)}
                {a.cancelReason && <> — "{a.cancelReason}"</>}
              </div>
            )}
            {a.status === 'reviewed_complete' && currentSubmission?.reviewNote && (
              <div className="mt-2 bg-green-50 rounded-md p-3 text-xs text-green-800">
                HR noted: {currentSubmission.reviewNote}
              </div>
            )}

            {/* October 10 18:22 addendum: the original blank form is retained exactly as it
                was and never deleted — it just moves OUT of the routine completed/archived
                view and under a collapsed "Record details" reference, available whenever HR
                genuinely needs it (e.g. to confirm what was actually assigned). Pending
                assignments never show this block — their blank form is already the primary
                action above. */}
            {(hasCompletedCopy || equipmentSignedMissing) && (
              <details className="mt-2 text-xs" data-testid="record-details">
                <summary className="text-gray-400 cursor-pointer hover:text-gray-600">Record details</summary>
                <div className="mt-1.5 flex gap-3 items-center flex-wrap pl-3">
                  <span className="text-gray-400">{originalLabel}:</span>
                  {fileValidity[templateKey] && !fileValidity[templateKey].valid ? (
                    <span className="text-gray-400 italic">Unavailable — {fileValidity[templateKey].reason}</span>
                  ) : (
                    <>
                      <button type="button" onClick={() => handleOpen(templateKey, (d) => downloadHrDocumentTemplate(a.assignmentId, d), 'inline')} className="text-gray-500 hover:underline">
                        View
                      </button>
                      <button type="button" onClick={() => handleOpen(templateKey, (d) => downloadHrDocumentTemplate(a.assignmentId, d), 'attachment')} className="text-gray-500 hover:underline">
                        Download
                      </button>
                    </>
                  )}
                  {downloadNotice[templateKey] && <span className="text-amber-700">{downloadNotice[templateKey]}</span>}
                </div>
              </details>
            )}

            {/* October 10 18:22 addendum: renamed "Move to History…" -> "Archive record…" —
                only offered on a completed, NOT-already-archived record. Opens the predefined-
                reason dialog; never a free-text/"Other" choice. */}
            {a.status === 'reviewed_complete' && !a.movedToHistoryAt && (
              <div className="mt-2">
                <button type="button" onClick={() => setMoveToHistoryTarget(a)} className="text-xs text-gray-500 hover:text-gray-700 underline">
                  Archive record…
                </button>
              </div>
            )}

            {/* October 10 18:22 addendum: an archived record shows the reason/detail/actor/
                time, keeps its completed-copy View/Download actions (already rendered above),
                and offers a confirmed, audited Restore — with the outcome shown inline. */}
            {a.movedToHistoryAt && (
              <HistoryEventDetail
                assignment={a}
                events={historyEvents[a.assignmentId]}
                onLoad={() => loadHistoryEvents(a.assignmentId)}
                correctionNote={correctionNote[a.assignmentId] ?? ''}
                onCorrectionNoteChange={(v) => setCorrectionNote((m) => ({ ...m, [a.assignmentId]: v }))}
                onRestore={() => handleRestore(a.assignmentId)}
                restoring={restoringId === a.assignmentId}
                feedback={restoreFeedback[a.assignmentId]}
              />
            )}
          </div>
        )}
      </li>
    );
  }

  return (
    <div className="space-y-4">
    {backToSetup && (
      <button type="button" className="text-sm text-[#176A9B] hover:underline" onClick={() => navigate(backToSetup)} data-testid="documents-back-to-setup">
        ← Back to setup
      </button>
    )}
    {/* BUILD-042 equipment checkout editor (draft -> Generate & assign). */}
    {!hideEquipment && <EquipmentCheckoutPanel employeeId={employeeId} callerEmployeeId={callerEmployeeId} onAssigned={refresh} />}

    {/* UAT layout (2026-10-13, Owner-requested): the three record groups — Needs attention,
        Active documents (reviewed-complete, not archived), and Archived records — are each in
        their OWN card so they read as distinct boxes rather than one stacked wall. Needs
        attention leads as the main focus, with a yellow ATTENTION badge; the Documents header /
        Assign control sits beneath it, then Active documents, then Archived records. Behavior,
        statuses, review gates, archive/restore and history semantics are unchanged — this is
        presentation only (renderRow and every handler/modal are untouched). */}

    {/* TOP (admin view) — Documents header + Assign control + section status + "How this works".
        Assigning is the admin's primary entry action, so it leads. */}
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
      <CardTitle
        action={
          <div className="flex gap-2 items-center">
            <select
              className="border border-gray-300 rounded px-3 py-1.5 text-sm"
              value={selectedTemplateId}
              disabled={cannotAssign}
              data-testid="admin-assign-template-select"
              onChange={(e) => { setSelectedTemplateId(e.target.value); setAssignValues({}); }}
            >
              <option value="">{templatesView.kind === 'loading' ? 'Loading documents…' : templatesView.kind === 'denied' ? 'Documents not available to you' : templatesView.kind === 'unavailable' ? 'Documents could not be loaded' : 'Choose a document…'}</option>
              {templates.map((t) => (
                <option key={t.templateId} value={t.templateId}>{t.name} (v{t.versions[0]?.versionNumber ?? '—'})</option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAssign}
              disabled={!selectedTemplateId || assigning || cannotAssign || missingAssignmentValues(chosenPrompts, assignValues).length > 0}
              title={cannotAssign ? assignPermissionMessage : undefined}
              data-testid="admin-assign-button"
              className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {assigning ? 'Assigning…' : 'Assign'}
            </button>
          </div>
        }
      >
        Documents
      </CardTitle>

      {(chosenPrompts.length > 0 || (chosenSummary?.employeeSupplies.length ?? 0) > 0 || (chosenSummary?.recordSupplies.length ?? 0) > 0) && (
        <div className="mb-3"><AssignValueInputs prompts={chosenPrompts} summary={chosenSummary} values={assignValues} onChange={setAssignValues} idPrefix="admin-assign" /></div>
      )}

      {status && <div className="text-sm text-gray-600 mb-3">{status}</div>}

      {cannotAssign && <div className="text-xs text-amber-800 mb-3" data-testid="documents-assign-needs-permission">{assignPermissionMessage}</div>}
      {(templatesView.kind === 'denied' || templatesView.kind === 'unavailable') && !(assignmentsView.kind === 'denied' && templatesView.kind === 'denied') && (
        <div className="mb-3"><LoadViewNotice view={templatesView} thing="the document list" permission={PERMISSION_LABEL.documents} onRetry={refresh} testIdPrefix="documents-templates" /></div>
      )}
      {assignmentsView.kind === 'loading' && <div className="text-sm text-gray-400 py-2" data-testid="documents-loading">Loading…</div>}
      {(assignmentsView.kind === 'denied' || assignmentsView.kind === 'targetUnavailable' || assignmentsView.kind === 'unavailable') && (
        <div className="mb-2"><LoadViewNotice view={assignmentsView} thing="this employee's documents" permission={PERMISSION_LABEL.documents} onRetry={refresh} testIdPrefix="documents-assignments" /></div>
      )}
      {assignmentsView.kind === 'ready' && assignments.length === 0 && (
        <div className="text-sm text-gray-400 py-2">No documents assigned yet.</div>
      )}

      <details className="text-xs text-gray-500 mt-4">
        <summary className="text-[#4AABDE] cursor-pointer">How this works</summary>
        <div className="mt-2 max-w-lg space-y-1.5">
          <p>HR assigns a reusable document template. The employee downloads/prints it, signs on paper, and uploads a scan — or HR can upload a scan it received directly, on the employee's behalf. An upload alone does not count as complete — HR must review it.</p>
          <p>Each group is shown in its own box. Click a row to expand it. The yellow "Needs your review" box appears only when a submitted or signed document is waiting for HR; "Waiting on the employee" lists documents the employee still has to sign or return. "Active documents" are reviewed and complete, and stay visible as the current record. "Archived records" holds assignments that were actually cancelled, or that HR explicitly archived with a reason — these are shown with distinct "Archived" and "Cancelled" labels, since they are different outcomes.</p>
          <p>On a completed or archived record, View/Download opens the actual signed/completed copy HR reviewed — that is always the primary file shown. The original blank form is kept too, exactly as it was, under the collapsed "Record details" reference.</p>
          <p>"Archive record" only changes where a completed record is shown — it keeps every file and never undoes the review. If it was archived by mistake, "Restore" reverses that one action and preserves the original record it was tied to — but Restore is correctly refused when it would silently override a still-active replacement/duplicate, or reverse a confirmed equipment return.</p>
          <p>Assigning the same document to someone who already has an outstanding (not-yet-reviewed) copy of it is blocked, to avoid accidental duplicates from a double-click. If an assignment was made by mistake, use "Cancel this assignment" — this reverses it without deleting any history, and never counts as reviewed complete.</p>
        </div>
      </details>
    </div>

    {/* Next — Needs attention (actionable). Yellow attention styling + an ATTENTION badge draw
        the eye. Always expanded. Only shown once at least one document is assigned. */}
    {/* Oct 17: the yellow warning appears ONLY when HR has something to do (a submitted or signed document to review). At zero it is not
        drawn at all - a warning that says "nothing needs attention" is a contradiction. Documents still waiting on the EMPLOYEE are
        listed separately, in a neutral box, because they are not HR's action. */}
    {needsReview.length > 0 && (
      <div className="bg-amber-50 rounded-lg border border-amber-300 shadow-sm p-5" role="region" aria-label="Documents waiting for your review" data-testid="needs-review-box">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-900 bg-amber-300 rounded px-1.5 py-0.5">Attention</span>
          <h3 className="text-xs font-semibold text-amber-900 uppercase tracking-wide">
            Needs your review <span className="text-amber-700">({needsReview.length})</span>
          </h3>
        </div>
        <ul className="divide-y divide-amber-200">
          {needsReview.map(renderRow)}
        </ul>
      </div>
    )}
    {waitingOnEmployee.length > 0 && (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5" data-testid="waiting-on-employee-box">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Waiting on the employee <span className="text-gray-400">({waitingOnEmployee.length})</span>
        </h3>
        <ul className="divide-y divide-gray-100">
          {waitingOnEmployee.map(renderRow)}
        </ul>
      </div>
    )}

    {/* Box 2 — Active documents (reviewed-complete and NOT archived — the current records).
        Its own card; collapsible, open by default. */}
    {completed.length > 0 && (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
        <button
          type="button"
          onClick={() => setCompletedOpen((v) => !v)}
          className="text-xs font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700"
        >
          Active documents ({completed.length}) {completedOpen ? '▾' : '▸'}
        </button>
        {completedOpen && (
          <ul className="divide-y divide-gray-100 mt-2">
            {completed.map(renderRow)}
          </ul>
        )}
      </div>
    )}

    {/* Box 3 — Archived records (actually cancelled OR explicitly archived). Its own card;
        collapsible, closed by default. Archived and Cancelled stay visibly distinguished by
        their own badge/wording inside renderRow — grouping is about placement only. */}
    {archivedRecords.length > 0 && (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          className="text-xs font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700"
        >
          Archived records ({archivedRecords.length}) {historyOpen ? '▾' : '▸'}
        </button>
        {historyOpen && (
          <ul className="divide-y divide-gray-100 mt-2">
            {archivedRecords.map(renderRow)}
          </ul>
        )}
      </div>
    )}

      {moveToHistoryTarget && (
        <MoveToHistoryModal
          assignment={moveToHistoryTarget}
          employeeId={employeeId}
          isEquipmentTemplate={templates.find((t) => t.templateId === moveToHistoryTarget.templateId)?.recordKind === 'equipment'}
          onClose={() => setMoveToHistoryTarget(null)}
          onMoved={(message) => { setStatus(message); refresh(); }}
        />
      )}
    </div>
  );
}
